"use client"

import { useState, useMemo, useEffect, useCallback, useRef, type MutableRefObject } from "react"
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"

import {
  cacheGalleryPostForDetail,
  dedupeGalleryPosts,
  useGalleryPosts,
} from "@/features/gallery/hooks/queries/use-gallery"
import { GalleryCard } from "@/features/gallery/components/gallery-card"
import { GallerySkeleton, SkeletonCard } from "@/features/gallery/components/gallery-skeleton"
import { FloatingGenerateBar } from "@/app/(app)/components/floating-generate-bar"
import { useAuth } from "@/features/auth/hooks/use-auth"
import { track } from "@/shared/lib/analytics"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"
import { cn } from "@/shared/lib/utils"
import { useUIStore } from "@/shared/stores/ui-store"

import { decideExploreLoad } from "./explore-renders"

const TABS = [
  { value: "for-you", labelKey: "forYou" },
  { value: "trending", labelKey: "trending" },
  { value: "new", labelKey: "new" },
  { value: "images", labelKey: "images" },
] as const

type TabValue = (typeof TABS)[number]["value"]
const EXPLORE_SCROLL_STATE_KEY = "pleasurai:explore-scroll-state"
const EXPLORE_RESTORE_REQUEST_KEY = "pleasurai:explore-restore-requested"
const EXPLORE_SCROLL_STATE_TTL_MS = 10 * 60 * 1000

interface ExploreScrollState {
  url: string
  mainScrollTop: number
  windowScrollY: number
  savedAt: number
}

// Distribute posts into columns for masonry layout
function distributeIntoColumns<T extends { aspectRatio: string }>(
  items: T[],
  numColumns: number
): T[][] {
  const columns: T[][] = Array.from({ length: numColumns }, () => [])
  const columnHeights: number[] = Array.from({ length: numColumns }, () => 0)

  const heightMap: Record<string, number> = {
    square: 1,
    portrait: 1.33,
    landscape: 0.75,
  }

  items.forEach((item) => {
    // Find the shortest column
    const shortestIndex = columnHeights.indexOf(Math.min(...columnHeights))
    columns[shortestIndex].push(item)
    columnHeights[shortestIndex] += heightMap[item.aspectRatio] || 1
  })

  return columns
}

function getTagFromUrl(): string | null {
  if (typeof window === "undefined") return null
  return new URLSearchParams(window.location.search).get("tag")
}

function isTabValue(value: string | null): value is TabValue {
  return TABS.some((tab) => tab.value === value)
}

function getTabFromUrl(): TabValue {
  if (typeof window === "undefined") return "for-you"
  const tab = new URLSearchParams(window.location.search).get("tab")
  return isTabValue(tab) ? tab : "for-you"
}

function buildExploreUrl(tab: TabValue, tag: string | null): string {
  const params = new URLSearchParams()
  if (tab !== "for-you") params.set("tab", tab)
  if (tag) params.set("tag", tag)
  const query = params.toString()
  return query ? `/explore?${query}` : "/explore"
}

function buildExplorePostUrl(postId: string, tab: TabValue, tag: string | null): string {
  const params = new URLSearchParams()
  if (tab !== "for-you") params.set("tab", tab)
  if (tag) params.set("tag", tag)
  const query = params.toString()
  return query ? `/explore/${postId}?${query}` : `/explore/${postId}`
}

function getExploreScrollContainer(root: HTMLDivElement | null): HTMLElement | null {
  return root?.closest("main") ?? null
}

function getCurrentPathWithSearch(): string {
  return `${window.location.pathname}${window.location.search}`
}

function saveExploreScrollState(root: HTMLDivElement | null) {
  if (typeof window === "undefined") return

  const scrollContainer = getExploreScrollContainer(root)
  const state: ExploreScrollState = {
    url: getCurrentPathWithSearch(),
    mainScrollTop: scrollContainer?.scrollTop ?? 0,
    windowScrollY: window.scrollY,
    savedAt: Date.now(),
  }

  try {
    window.sessionStorage.setItem(EXPLORE_SCROLL_STATE_KEY, JSON.stringify(state))
    window.sessionStorage.setItem(EXPLORE_RESTORE_REQUEST_KEY, "1")
  } catch {
    // Scroll restoration is a small UX enhancement; navigation should still work.
  }
}

function readExploreScrollState(): ExploreScrollState | null {
  if (typeof window === "undefined") return null

  try {
    if (!window.sessionStorage.getItem(EXPLORE_RESTORE_REQUEST_KEY)) return null

    const raw = window.sessionStorage.getItem(EXPLORE_SCROLL_STATE_KEY)
    if (!raw) return null

    const state = JSON.parse(raw) as ExploreScrollState
    const isFresh = Date.now() - state.savedAt < EXPLORE_SCROLL_STATE_TTL_MS
    const isCurrentExploreUrl = state.url === getCurrentPathWithSearch()
    return isFresh && isCurrentExploreUrl ? state : null
  } catch {
    return null
  }
}

function clearExploreRestoreRequest() {
  if (typeof window === "undefined") return

  try {
    window.sessionStorage.removeItem(EXPLORE_RESTORE_REQUEST_KEY)
  } catch {
    // Ignore storage failures.
  }
}

export function ExploreContent() {
  const t = useTranslations("explore")
  const router = useLocalizedRouter()
  const prefersReducedMotion = useReducedMotion()
  const [activeTab, setActiveTab] = useState<TabValue>("for-you")
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [hasSyncedUrlState, setHasSyncedUrlState] = useState(false)
  const [guestFeedLocked, setGuestFeedLocked] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const pendingScrollRestoreRef = useRef<ExploreScrollState | null>(null)
  const didTrackSessionStartRef = useRef(false)
  const { user, profile, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const setAuthContext = useUIStore((s) => s.setAuthContext)
  const viewer = useMemo(
    () => ({
      userId: user?.id ?? null,
      preferences: profile?.contentPreferences ?? null,
      // The feed is public. Start it as soon as URL state is known instead of
      // holding first paint behind auth validation; React Query will move to
      // the identified cache key if a session is restored moments later.
      isLoading: !hasSyncedUrlState,
    }),
    [user?.id, profile?.contentPreferences, hasSyncedUrlState]
  )

  // Sync state from URL after mount to avoid SSR/client hydration mismatch
  useEffect(() => {
    const resetScrollAfterPopState = () => {
      const reset = () => {
        const scrollContainer = getExploreScrollContainer(rootRef.current)
        scrollContainer?.scrollTo({ top: 0, behavior: "auto" })
        window.scrollTo({ top: 0, behavior: "auto" })
      }

      requestAnimationFrame(reset)
    }

    const syncStateFromUrl = (resetScroll = false) => {
      setActiveTab(getTabFromUrl())
      setActiveTag(getTagFromUrl())
      if (resetScroll) resetScrollAfterPopState()
    }

    syncStateFromUrl()
    pendingScrollRestoreRef.current = readExploreScrollState()
    setHasSyncedUrlState(true)

    const handlePopState = () => syncStateFromUrl(true)
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [])

  useEffect(() => {
    setGuestFeedLocked(false)
  }, [activeTab, activeTag, isAuthenticated])

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useGalleryPosts(activeTab, activeTag, viewer)
  const isPageLoading = !hasSyncedUrlState || isLoading

  // Track feed session start after the URL-selected tab is known.
  useEffect(() => {
    if (!hasSyncedUrlState || didTrackSessionStartRef.current) return
    didTrackSessionStartRef.current = true
    track("feed:session_start", { tab: activeTab })
  }, [activeTab, hasSyncedUrlState])

  const posts = useMemo(
    () =>
      dedupeGalleryPosts(data?.pages.flatMap((page) => page.posts) ?? []),
    [data]
  )

  const loadedPageCount = data?.pages.length ?? 0

  const openGuestExploreGate = useCallback(() => {
    setGuestFeedLocked(true)
    setAuthContext({
      characterId: null,
      characterName: null,
      characterAvatar: null,
      source: "generic",
    })
    setActiveModal("register")
    track("feed:guest_limit_reached", {
      tab: activeTab,
      loaded_pages: loadedPageCount,
    })
  }, [activeTab, loadedPageCount, setActiveModal, setAuthContext])

  // Five columns on wide screens, with CSS hiding extra columns at smaller breakpoints.
  const columns = useMemo(
    () => distributeIntoColumns(posts, 5),
    [posts]
  )

  const itemInitial = useMemo(
    () => ({ opacity: 0, y: prefersReducedMotion ? 0 : 12 }),
    [prefersReducedMotion]
  )

  const itemAnimate = useMemo(
    () => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0.12 : 0.25,
        ease: "easeOut" as const,
      },
    }),
    [prefersReducedMotion]
  )

  // ── Infinite scroll via IntersectionObserver ──
  // Use refs so the observer callback always reads fresh values without
  // disconnecting/reconnecting on every fetch cycle.
  const scrollStateRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage, isAuthenticated, isAuthLoading, loadedPageCount, guestFeedLocked, openGuestExploreGate }) as MutableRefObject<{
    hasNextPage: boolean | undefined
    isFetchingNextPage: boolean
    fetchNextPage: typeof fetchNextPage
    isAuthenticated: boolean
    isAuthLoading: boolean
    loadedPageCount: number
    guestFeedLocked: boolean
    openGuestExploreGate: () => void
  }>
  scrollStateRef.current = {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isAuthenticated,
    isAuthLoading,
    loadedPageCount,
    guestFeedLocked,
    openGuestExploreGate,
  }

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        const {
          hasNextPage: hnp,
          isFetchingNextPage: ifnp,
          fetchNextPage: fnp,
          isAuthenticated: authed,
          isAuthLoading: authLoading,
          loadedPageCount: pagesLoaded,
          guestFeedLocked: guestLocked,
          openGuestExploreGate: openGuestGate,
        } =
          scrollStateRef.current
        if (entry.isIntersecting && hnp && !ifnp) {
          const decision = decideExploreLoad({ isAuthenticated: authed, isAuthLoading: authLoading, loadedPageCount: pagesLoaded })
          if (decision === "wait") return
          if (decision === "gate") {
            if (!guestLocked) openGuestGate()
            return
          }

          fnp()
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px 400px 0px" }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [activeTab, activeTag, isLoading, isAuthLoading]) // re-connect when data loads and sentinel appears, and re-check the gate once auth settles

  const openPost = useCallback((post: (typeof posts)[number]) => {
    saveExploreScrollState(rootRef.current)
    cacheGalleryPostForDetail(post)
    const postUrl = buildExplorePostUrl(post.id, activeTab, activeTag)

    track("feed:item_view", { post_id: post.id, source: "feed" })
    router.push(postUrl)
  }, [activeTab, activeTag, router])

  const scrollFeedToTop = useCallback(() => {
    const behavior: ScrollBehavior = prefersReducedMotion ? "auto" : "smooth"
    const scrollContainer = getExploreScrollContainer(rootRef.current)

    scrollContainer?.scrollTo({ top: 0, behavior })
    window.scrollTo({ top: 0, behavior })
  }, [prefersReducedMotion])

  const handleTabChange = useCallback((tab: TabValue) => {
    if (tab === activeTab) return

    setActiveTab(tab)
    setGuestFeedLocked(false)
    window.history.pushState(null, "", router.localize(buildExploreUrl(tab, activeTag)))
    scrollFeedToTop()
  }, [activeTab, activeTag, router, scrollFeedToTop])

  useEffect(() => {
    const scrollState = pendingScrollRestoreRef.current
    if (!scrollState || isPageLoading || posts.length === 0) return

    pendingScrollRestoreRef.current = null

    const restoreScroll = () => {
      const scrollContainer = getExploreScrollContainer(rootRef.current)
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollState.mainScrollTop
      }
      window.scrollTo(0, scrollState.windowScrollY)
    }

    requestAnimationFrame(() => {
      restoreScroll()
      window.setTimeout(restoreScroll, 80)
    })
    clearExploreRestoreRequest()
  }, [isPageLoading, posts.length])

  // Clear tag filter
  const clearTag = useCallback(() => {
    setActiveTag(null)
    window.history.pushState(null, "", router.localize(buildExploreUrl(activeTab, null)))
    scrollFeedToTop()
  }, [activeTab, router, scrollFeedToTop])

  return (
    <div ref={rootRef} className="-mx-4 -mt-4 min-h-screen bg-black md:-mx-6 md:-mt-6">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0.15 : 0.3 }}
        className="px-2 pb-3 pt-5 md:px-3"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-base font-semibold tracking-normal text-white/82">{t("title")}</h1>
          <div
            className="no-scrollbar -mx-2 overflow-x-auto px-2 sm:mx-0 sm:px-0"
            role="tablist"
            aria-label={t("title")}
          >
            <div className="inline-flex min-w-max gap-1 rounded-full border border-white/[0.08] bg-white/[0.04] p-1">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.value

                return (
                  <button
                    key={tab.value}
                    id={`explore-tab-${tab.value}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls="explore-feed"
                    onClick={() => handleTabChange(tab.value)}
                    className={cn(
                      "h-8 min-w-[72px] rounded-full px-3 text-sm font-medium leading-none text-white/58 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue/60",
                      "hover:bg-white/[0.08] hover:text-white",
                      isActive && "bg-white text-black hover:bg-white hover:text-black"
                    )}
                  >
                    {t(`tabs.${tab.labelKey}`)}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Active tag filter chip */}
      <AnimatePresence>
        {activeTag && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.1 : 0.2 }}
            className="px-2 pb-3 md:px-3"
          >
            <div className="flex items-center gap-2">
              <Icon name="lable" size={14} className="size-3.5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t("filteringBy")}</span>
              <button
                onClick={clearTag}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-foreground transition-colors hover:bg-white/20"
              >
                #{activeTag}
                <Icon name="x" size={12} className="size-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        id="explore-feed"
        role="tabpanel"
        aria-labelledby={`explore-tab-${activeTab}`}
      >
        {/* Initial loading skeleton */}
        {isPageLoading && <GallerySkeleton count={15} />}

      {/* Error state */}
      {!isPageLoading && isError && (
        <div className="mx-2 border border-destructive/30 bg-card/60 p-6 text-center md:mx-3">
          <div className="mx-auto flex size-10 items-center justify-center rounded-full bg-destructive/15 text-destructive">
            <Icon name="attention-circle" size={20} className="size-5" />
          </div>
          <p className="mt-3 text-base font-semibold">{t("errorTitle")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("errorDescription")}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 inline-flex border border-border bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-card"
          >
            {t("retry")}
          </button>
        </div>
      )}

      {/* Masonry Grid (column-based layout) */}
      {!isPageLoading && !isError && posts.length > 0 && (
        <>
          <div
            key={`masonry-${activeTab}-${activeTag ?? ""}`}
            className="flex gap-px"
          >
            {columns.map((column, colIndex) => (
              <div
                key={colIndex}
                className={cn(
                  "flex flex-1 flex-col gap-px",
                  colIndex >= 2 && "hidden sm:flex",
                  colIndex >= 3 && "hidden md:flex",
                  colIndex >= 4 && "hidden xl:flex",
                )}
              >
                {column.map((post, itemIndex) => {
                  const isInitialViewportCard = itemIndex === 0 && colIndex < 2
                  return (
                  <motion.div
                    key={post.id}
                    initial={isInitialViewportCard ? false : itemInitial}
                    whileInView={isInitialViewportCard ? undefined : itemAnimate}
                    viewport={{ once: true, margin: "100px" }}
                  >
                    <GalleryCard
                      post={post}
                      priority={isInitialViewportCard}
                      onClick={() => openPost(post)}
                    />
                  </motion.div>
                  )
                })}
                {/* Inline skeleton cards at the bottom of each column while loading */}
                {isFetchingNextPage &&
                  Array.from({ length: 2 }, (_, i) => (
                    <SkeletonCard
                      key={`skel-${colIndex}-${i}`}
                      index={colIndex * 2 + i}
                      prefersReducedMotion={prefersReducedMotion}
                    />
                  ))}
              </div>
            ))}
          </div>

          {/* Sentinel for infinite scroll — always rendered so observer stays connected */}
          <div ref={sentinelRef} className="h-4" aria-hidden="true" />

          {/* End of feed indicator */}
          {guestFeedLocked && !isAuthenticated && !isAuthLoading ? (
            <div className="flex justify-center px-4 pb-8 pt-2">
              <button
                type="button"
                onClick={openGuestExploreGate}
                className="rounded-full border border-white/[0.08] bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
              >
                {t("guestGateCta")}
              </button>
            </div>
          ) : !hasNextPage && posts.length > 0 ? (
            <p className="pb-8 text-center text-sm text-muted-foreground/50">
              {t("seenAll")}
            </p>
          ) : null}
          </>
        )}

      {/* Empty state */}
      {!isPageLoading && !isError && posts.length === 0 && (
        <div className="flex flex-col items-center justify-center px-4 py-20 text-center">
          <p className="text-lg font-medium text-muted-foreground">
            {t("emptyTitle")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            {t("emptyDescription")}
          </p>
        </div>
      )}

        {/* Bottom spacer so content isn't hidden behind floating bar */}
        <div className="h-40" aria-hidden="true" />
      </div>

      {/* Floating generate prompt bar */}
      <FloatingGenerateBar />

    </div>
  )
}
